"use client";

import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import {
  canCreateEvents,
  createEvent,
  getAdminEvents,
  getCommunityEvents,
  getEventAttendees,
  getEventById,
  getEventBySlug,
  getMyEvents,
  getPastEvents,
  getUpcomingEvents,
  prepareEventReminders,
  removeEventCover,
  setEventAttendance,
  setEventLifecycle,
  shareEventToFeed,
  softDeleteEvent,
  updateEvent,
  uploadEventCover,
} from "@/lib/events/api";
import type {
  EventAttendanceStatus,
  EventCreateInput,
  EventListFilters,
  EventStatus,
  EventUpdateInput,
} from "@/lib/events/types";
import { EVENT_PAGE_SIZE } from "@/lib/events/types";
import { queryKeys } from "@/lib/query-keys";
import { getErrorMessage } from "@/lib/utils";

async function invalidateEventQueries(
  queryClient: ReturnType<typeof useQueryClient>,
  extra?: { slug?: string; eventId?: string; communityId?: string | null },
) {
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: queryKeys.events }),
    extra?.slug
      ? queryClient.invalidateQueries({ queryKey: queryKeys.event(extra.slug) })
      : Promise.resolve(),
    extra?.eventId
      ? queryClient.invalidateQueries({
          queryKey: queryKeys.eventById(extra.eventId),
        })
      : Promise.resolve(),
    extra?.eventId
      ? queryClient.invalidateQueries({
          queryKey: queryKeys.eventAttendees(extra.eventId),
        })
      : Promise.resolve(),
    extra?.communityId
      ? queryClient.invalidateQueries({
          queryKey: queryKeys.communityEvents(extra.communityId),
        })
      : Promise.resolve(),
    queryClient.invalidateQueries({ queryKey: queryKeys.feed }),
    queryClient.invalidateQueries({ queryKey: queryKeys.notifications }),
  ]);
}

export function useCanCreateEvents(enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.canCreateEvents,
    queryFn: canCreateEvents,
    enabled,
  });
}

export function useUpcomingEvents(filters: EventListFilters, enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.upcomingEvents(filters),
    queryFn: () => getUpcomingEvents(filters),
    enabled,
  });
}

export function usePastEvents(filters: EventListFilters, enabled: boolean) {
  return useInfiniteQuery({
    queryKey: queryKeys.pastEvents(filters),
    queryFn: ({ pageParam }) =>
      getPastEvents({ filters, offset: pageParam }),
    initialPageParam: 0,
    getNextPageParam: (lastPage, pages) =>
      lastPage.length < EVENT_PAGE_SIZE
        ? undefined
        : pages.length * EVENT_PAGE_SIZE,
    enabled,
  });
}

export function useMyEvents(enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.myEvents,
    queryFn: getMyEvents,
    enabled,
  });
}

export function useCommunityEvents(communityId: string, enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.communityEvents(communityId),
    queryFn: () => getCommunityEvents(communityId),
    enabled: enabled && Boolean(communityId),
  });
}

export function useEvent(slug: string, enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.event(slug),
    queryFn: () => getEventBySlug(slug),
    enabled: enabled && Boolean(slug),
  });
}

export function useEventById(eventId: string, enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.eventById(eventId),
    queryFn: () => getEventById(eventId),
    enabled: enabled && Boolean(eventId),
  });
}

export function useEventAttendees(eventId: string, enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.eventAttendees(eventId),
    queryFn: () => getEventAttendees(eventId),
    enabled: enabled && Boolean(eventId),
  });
}

export function useAdminEvents(enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.adminEvents,
    queryFn: getAdminEvents,
    enabled,
  });
}

export function useSetEventAttendance(event: {
  id: string;
  slug: string;
  community?: { id: string } | null;
}) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (status: EventAttendanceStatus) =>
      setEventAttendance(event.id, status),
    onSuccess: async () => {
      await invalidateEventQueries(queryClient, {
        slug: event.slug,
        eventId: event.id,
        communityId: event.community?.id ?? null,
      });
    },
  });
}

export function useCreateEvent() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      userId: string;
      values: Omit<EventCreateInput, "coverPath">;
      cover: File | null;
    }) => {
      let coverPath: string | null = null;
      if (input.cover) {
        coverPath = await uploadEventCover({
          userId: input.userId,
          file: input.cover,
        });
      }
      try {
        return await createEvent({ ...input.values, coverPath });
      } catch (error) {
        const cleanupError = await removeEventCover(coverPath);
        if (cleanupError) {
          throw new Error(
            `${getErrorMessage(error)} A borítókép takarítása is sikertelen: ${getErrorMessage(cleanupError)}`,
          );
        }
        throw error;
      }
    },
    onSuccess: async (event) => {
      await invalidateEventQueries(queryClient, {
        slug: event?.slug,
        eventId: event?.id,
        communityId: event?.community?.id ?? null,
      });
    },
  });
}

export function useUpdateEvent(slug: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      userId: string;
      values: Omit<EventUpdateInput, "coverPath">;
      cover: File | null;
      existingCoverPath: string | null;
    }) => {
      let coverPath = input.existingCoverPath;
      if (input.cover) {
        coverPath = await uploadEventCover({
          userId: input.userId,
          file: input.cover,
        });
      }
      try {
        await updateEvent({ ...input.values, coverPath });
        return coverPath;
      } catch (error) {
        if (input.cover && coverPath && coverPath !== input.existingCoverPath) {
          await removeEventCover(coverPath);
        }
        throw error;
      }
    },
    onSuccess: async (_path, input) => {
      await invalidateEventQueries(queryClient, {
        slug,
        eventId: input.values.eventId,
      });
    },
  });
}

export function useSetEventLifecycle(event: {
  id: string;
  slug: string;
  community?: { id: string } | null;
}) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (status: EventStatus) => setEventLifecycle(event.id, status),
    onSuccess: async () => {
      await invalidateEventQueries(queryClient, {
        slug: event.slug,
        eventId: event.id,
        communityId: event.community?.id ?? null,
      });
    },
  });
}

export function useSoftDeleteEvent(event: {
  id: string;
  slug: string;
  community?: { id: string } | null;
}) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => softDeleteEvent(event.id),
    onSuccess: async () => {
      await invalidateEventQueries(queryClient, {
        slug: event.slug,
        eventId: event.id,
        communityId: event.community?.id ?? null,
      });
    },
  });
}

export function useShareEvent(event: {
  id: string;
  community?: { id: string } | null;
}) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      content: string;
      audience: "global" | "community";
      communityId: string | null;
    }) =>
      shareEventToFeed({
        eventId: event.id,
        ...input,
      }),
    onSuccess: async (_postId, input) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: queryKeys.feed }),
        input.communityId
          ? queryClient.invalidateQueries({
              queryKey: queryKeys.communityFeed(input.communityId),
            })
          : Promise.resolve(),
        event.community?.id
          ? queryClient.invalidateQueries({
              queryKey: queryKeys.communityFeed(event.community.id),
            })
          : Promise.resolve(),
      ]);
    },
  });
}

export function usePrepareEventReminders() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: prepareEventReminders,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.notifications });
    },
  });
}
